import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import gsap from 'gsap';

@Component({
  selector: 'vx-home-alternative',
  standalone: true,
  imports: [RouterLink, LucideAngularModule],
  template: `
    <section class="alt-home" #pageRoot>
      <section class="cinematic-hero">
        <video
          #heroVideo
          class="hero-video"
          [src]="videos[activeVideo()].src"
          autoplay
          muted
          playsinline
          preload="metadata"
          (ended)="playNextVideo()"
          (loadedmetadata)="playCurrentVideo()"
        ></video>
        <div class="hero-shade"></div>

        <div class="container hero-grid">
          <div class="hero-copy">
            <div class="brand-lockup">
              <img src="assets/brand/vaultrix-logo.jpeg" alt="VaulTriX">
              <span>VaulTriX</span>
            </div>
            <h1>Il Futuro della Sicurezza Urbana e dello Storage Intelligente</h1>
            <p>
              Molto più di un deposito bagagli: VaulTriX è una nuova infrastruttura di sicurezza urbana
              progettata per proteggere beni personali, oggetti di valore e materiali sensibili all'interno
              delle città moderne, con accessi digitali, monitoraggio avanzato e tracciabilità.
            </p>

            <div class="hero-actions">
              <a routerLink="/demo" class="btn primary">Richiedi una demo</a>
              <a routerLink="/soluzioni" class="btn ghost">Scopri le soluzioni</a>
            </div>

            <div class="signal-strip" aria-label="Punti chiave VaulTriX">
              @for (metric of heroSignals; track metric.label) {
                <article>
                  <strong>{{ metric.value }}</strong>
                  <span>{{ metric.label }}</span>
                </article>
              }
            </div>
          </div>

          <div class="command-panel" aria-label="Dashboard VaulTriX">
            <div class="panel-top">
              <div>
                <span>VX Urban Grid</span>
                <small>Rete live di stazioni Security Hub</small>
              </div>
              <i>Live</i>
            </div>
            <div class="status-map">
              <div class="map-zone station">Stazione</div>
              <div class="map-zone center">Centro</div>
              <div class="map-zone airport">Aeroporto</div>
              <div class="central-hub" aria-hidden="true">
                <lucide-icon name="hard-drive" [size]="24" />
                <strong>VX Hub</strong>
                <span>Security control</span>
              </div>
              @for (node of cityNodes; track node.name) {
                <button
                  type="button"
                  class="map-node"
                  [class.alert]="node.state === 'alert'"
                  [class.busy]="node.state === 'busy'"
                  [class.selected]="selectedNode().name === node.name"
                  [style.left.%]="node.x"
                  [style.top.%]="node.y"
                  [attr.aria-label]="node.name + ': ' + node.label"
                  [attr.aria-pressed]="selectedNode().name === node.name"
                  (click)="selectNode(node)"
                >
                  <span class="luggage-marker" aria-hidden="true">
                    <lucide-icon name="baggage-claim" [size]="18" />
                  </span>
                  <span>{{ node.name }}</span>
                  <small>{{ node.label }}</small>
                </button>
              }
              <div class="route-line one"></div>
              <div class="route-line two"></div>
              <div class="route-line three"></div>
            </div>
            <div class="map-events">
              <article>
                <lucide-icon name="shield-check" [size]="17" />
                <span>Accessi tracciati</span>
              </article>
              <article>
                <lucide-icon name="qr-code" [size]="17" />
                <span>QR / PIN attivi</span>
              </article>
              <article class="warning">
                <lucide-icon name="radio-tower" [size]="17" />
                <span>1 nodo in verifica</span>
              </article>
            </div>
            <article class="node-detail" [class.alert]="selectedNode().state === 'alert'" [class.busy]="selectedNode().state === 'busy'">
              <div>
                <span>Stazione selezionata</span>
                <strong>{{ selectedNode().name }}</strong>
              </div>
              <p>{{ selectedNode().detail }}</p>
              <small>{{ selectedNode().label }}</small>
            </article>
            <div class="telemetry-grid">
              @for (item of telemetry; track item.label) {
                <article>
                  <span>{{ item.label }}</span>
                  <strong>{{ item.value }}</strong>
                </article>
              }
            </div>
          </div>
        </div>
      </section>

      <section class="section problem-section">
        <div class="container problem-grid">
          <div>
            <span class="eyebrow">Perché nasce VaulTriX</span>
            <h2 class="section-title">La custodia temporanea diventa infrastruttura urbana.</h2>
          </div>
          <div class="problem-copy">
            <p>
              Ogni giorno turisti, studenti, professionisti e cittadini si muovono portando con sé bagagli,
              documenti, dispositivi elettronici e oggetti di valore.
            </p>
            <p>
              VaulTriX colma il limite delle soluzioni tradizionali con una rete di stazioni sicure, sempre
              disponibili e pronte a integrarsi con le future Smart City.
            </p>
            <div class="problem-goals">
              @for (goal of birthGoals; track goal) {
                <article>{{ goal }}</article>
              }
            </div>
          </div>
        </div>
      </section>

      <section class="security-hub">
        <div class="container">
          <div class="section-heading">
            <span class="eyebrow">Security Hub</span>
            <h2 class="section-title">Sicurezza progettata fin dall'origine.</h2>
            <p class="section-lead">
              Ogni stazione VaulTriX è sviluppata secondo il principio Security Hub: accesso digitale,
              autenticazione, monitoraggio intelligente e protezione contro utilizzi non autorizzati.
            </p>
          </div>

          <div class="hub-grid">
            @for (item of securityHub; track item.title) {
              <article class="hub-card">
                <div class="icon-badge dark"><lucide-icon [name]="item.icon" [size]="24" /></div>
                <h3>{{ item.title }}</h3>
                <p>{{ item.text }}</p>
              </article>
            }
          </div>
        </div>
      </section>

      <section class="video-command">
        <div class="container video-grid">
          <div>
            <span class="eyebrow">VaulTriX in azione</span>
            <h2 class="section-title">Un'esperienza digitale, rapida e sicura.</h2>
            <p class="section-lead">
              I video esistenti diventano parte della narrazione: deposito, accesso smart locker e utilizzo in
              contesti urbani ad alta affluenza.
            </p>
            <div class="video-tabs" aria-label="Seleziona video">
              @for (video of videos; track video.src; let i = $index) {
                <button type="button" [class.active]="i === activeVideo()" (click)="selectVideo(i)">
                  <lucide-icon name="play" [size]="16" />
                  <span>{{ video.title }}</span>
                </button>
              }
            </div>
          </div>

          <div class="video-frame">
            <video
              [src]="videos[activeVideo()].src"
              autoplay
              muted
              playsinline
              preload="metadata"
              (loadedmetadata)="playCurrentVideo()"
            ></video>
            <div class="video-caption">
              <span>{{ videos[activeVideo()].kicker }}</span>
              <strong>{{ videos[activeVideo()].title }}</strong>
            </div>
          </div>
        </div>
      </section>

      <section class="future-section">
        <div class="container future-panel">
          <span class="eyebrow">Il futuro della sicurezza urbana inizia qui</span>
          <h2>Una rete intelligente di protezione al servizio di cittadini, turisti e città.</h2>
          <p>
            VaulTriX combina tecnologia, sicurezza e innovazione per superare i tradizionali sistemi di deposito
            e trasformare ogni stazione in un punto attivo della Smart City.
          </p>
        </div>
      </section>

      <section class="mission-section">
        <div class="container mission-panel">
          <span class="eyebrow">La nostra missione</span>
          <h2>
            Trasformare la sicurezza degli oggetti personali in un'infrastruttura urbana intelligente.
          </h2>
          <p>
            VaulTriX supporta la mobilità, migliora l'esperienza dei cittadini e accelera l'evoluzione delle
            Smart City con una rete di protezione distribuita, digitale e scalabile.
          </p>
          <div class="mission-actions">
            <a routerLink="/demo" class="btn primary">Partecipa al progetto</a>
            <a routerLink="/come-funziona" class="btn ghost">Come funziona</a>
          </div>
        </div>
      </section>
    </section>
  `,
  styles: [`
    .alt-home {
      overflow: hidden;
      color: #eef7ff;
      background: #060b14;
    }
    .cinematic-hero {
      position: relative;
      min-height: calc(100vh - 78px);
      display: grid;
      align-items: center;
      overflow: hidden;
    }
    .hero-video,
    .hero-shade {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }
    .hero-video {
      object-fit: cover;
      filter: saturate(1.08) contrast(1.08);
    }
    .hero-shade {
      background:
        linear-gradient(90deg, rgba(4, 9, 18, 0.94), rgba(4, 11, 22, 0.74) 46%, rgba(3, 10, 18, 0.48)),
        radial-gradient(circle at 76% 26%, rgba(19, 216, 167, 0.16), transparent 22rem),
        radial-gradient(circle at 18% 78%, rgba(19, 93, 255, 0.2), transparent 24rem);
    }
    .hero-grid {
      position: relative;
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(340px, 0.8fr);
      gap: 42px;
      align-items: center;
      padding: 76px 0;
    }
    .brand-lockup {
      display: inline-flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 24px;
    }
    .brand-lockup img {
      width: 112px;
      height: 78px;
      object-fit: contain;
      mix-blend-mode: screen;
      filter: drop-shadow(0 16px 34px rgba(19, 216, 167, 0.18));
    }
    .brand-lockup span {
      color: #8defff;
      font-family: "Space Grotesk", sans-serif;
      font-size: clamp(1.6rem, 3vw, 2.45rem);
      font-weight: 900;
      text-transform: uppercase;
    }
    h1 {
      max-width: 800px;
      margin: 0;
      font-family: "Space Grotesk", sans-serif;
      font-size: clamp(2.45rem, 5.4vw, 5.25rem);
      line-height: 0.96;
      letter-spacing: 0;
    }
    .hero-copy p {
      max-width: 720px;
      margin: 24px 0 0;
      color: rgba(238, 247, 255, 0.78);
      font-size: clamp(1.05rem, 1.6vw, 1.24rem);
      font-weight: 750;
      line-height: 1.65;
    }
    .hero-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      margin-top: 30px;
    }
    .signal-strip {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 10px;
      margin-top: 34px;
      max-width: 760px;
    }
    .signal-strip article {
      min-height: 86px;
      padding: 14px;
      border-radius: 8px;
      border: 1px solid rgba(141, 239, 255, 0.18);
      background: rgba(255, 255, 255, 0.075);
      backdrop-filter: blur(16px);
    }
    .signal-strip strong {
      display: block;
      color: #13d8a7;
      font-family: "Space Grotesk", sans-serif;
      font-size: 1.34rem;
      line-height: 1;
    }
    .signal-strip span {
      display: block;
      margin-top: 8px;
      color: rgba(238, 247, 255, 0.74);
      font-size: 0.82rem;
      font-weight: 850;
    }
    .command-panel {
      position: relative;
      min-height: 540px;
      padding: 18px;
      border-radius: 8px;
      border: 1px solid rgba(141, 239, 255, 0.2);
      background:
        linear-gradient(180deg, rgba(8, 18, 34, 0.82), rgba(5, 11, 20, 0.94)),
        radial-gradient(circle at 50% 30%, rgba(19, 93, 255, 0.18), transparent 18rem);
      box-shadow: 0 28px 100px rgba(0, 0, 0, 0.34);
      backdrop-filter: blur(18px);
    }
    .panel-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: rgba(238, 247, 255, 0.8);
      font-weight: 900;
    }
    .panel-top div {
      display: grid;
      gap: 4px;
    }
    .panel-top span {
      color: white;
      font-family: "Space Grotesk", sans-serif;
      font-size: 1.05rem;
    }
    .panel-top small {
      color: rgba(238, 247, 255, 0.54);
      font-size: 0.78rem;
      font-weight: 850;
    }
    .panel-top i {
      position: relative;
      padding: 7px 10px 7px 24px;
      border-radius: 999px;
      color: #13d8a7;
      background: rgba(19, 216, 167, 0.12);
      font-style: normal;
      font-size: 0.78rem;
    }
    .panel-top i::before {
      content: "";
      position: absolute;
      left: 10px;
      top: 50%;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #13d8a7;
      transform: translateY(-50%);
      box-shadow: 0 0 18px rgba(19, 216, 167, 0.9);
    }
    .status-map {
      position: relative;
      height: 350px;
      margin: 20px 0 12px;
      overflow: hidden;
      border-radius: 8px;
      border: 1px solid rgba(141, 239, 255, 0.12);
      background:
        linear-gradient(rgba(141, 239, 255, 0.07) 1px, transparent 1px),
        linear-gradient(90deg, rgba(141, 239, 255, 0.07) 1px, transparent 1px),
        radial-gradient(circle at 52% 42%, rgba(19, 216, 167, 0.18), transparent 12rem),
        rgba(4, 11, 22, 0.88);
      background-size: 32px 32px, 32px 32px, auto, auto;
    }
    .map-zone {
      position: absolute;
      z-index: 1;
      padding: 7px 10px;
      border-radius: 999px;
      color: rgba(238, 247, 255, 0.62);
      background: rgba(255, 255, 255, 0.055);
      border: 1px solid rgba(141, 239, 255, 0.1);
      font-size: 0.72rem;
      font-weight: 900;
      text-transform: uppercase;
    }
    .map-zone.station { left: 8%; top: 10%; }
    .map-zone.center { left: 44%; top: 12%; }
    .map-zone.airport { right: 7%; bottom: 10%; }
    .central-hub {
      position: absolute;
      left: 50%;
      top: 52%;
      z-index: 3;
      width: 118px;
      min-height: 86px;
      display: grid;
      justify-items: center;
      align-content: center;
      gap: 4px;
      padding: 12px;
      border-radius: 8px;
      color: #07111f;
      background: linear-gradient(135deg, #8defff, #13d8a7);
      box-shadow: 0 0 0 10px rgba(19, 216, 167, 0.08), 0 20px 60px rgba(19, 216, 167, 0.18);
      transform: translate(-50%, -50%);
    }
    .central-hub strong {
      font-family: "Space Grotesk", sans-serif;
      font-size: 0.98rem;
      line-height: 1;
    }
    .central-hub span {
      color: rgba(7, 17, 31, 0.72);
      font-size: 0.68rem;
      font-weight: 900;
      text-transform: uppercase;
    }
    .map-node {
      position: absolute;
      z-index: 4;
      width: 122px;
      min-height: 62px;
      display: grid;
      grid-template-columns: 28px minmax(0, 1fr);
      grid-template-rows: auto auto;
      align-items: center;
      column-gap: 8px;
      border: 0;
      border-radius: 8px;
      padding: 9px 10px;
      cursor: pointer;
      transform: translate(-50%, -50%);
      color: #eef7ff;
      background: rgba(6, 17, 31, 0.88);
      border: 1px solid rgba(19, 216, 167, 0.42);
      box-shadow: 0 0 0 8px rgba(19, 216, 167, 0.07), 0 16px 36px rgba(0, 0, 0, 0.28);
      backdrop-filter: blur(10px);
    }
    .luggage-marker {
      grid-row: span 2;
      width: 32px;
      height: 32px;
      display: grid;
      place-items: center;
      border-radius: 999px;
      color: #07111f;
      background: #13d8a7;
      box-shadow: 0 0 22px rgba(19, 216, 167, 0.72);
    }
    .luggage-marker lucide-icon {
      color: #07111f;
      stroke-width: 2.7;
    }
    .map-node span {
      overflow: hidden;
      color: white;
      font-size: 0.76rem;
      font-weight: 950;
      line-height: 1.05;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .map-node small {
      color: rgba(238, 247, 255, 0.58);
      font-size: 0.68rem;
      font-weight: 850;
      line-height: 1.05;
    }
    .map-node.busy {
      border-color: rgba(255, 176, 32, 0.48);
      box-shadow: 0 0 0 8px rgba(255, 176, 32, 0.07), 0 16px 36px rgba(0, 0, 0, 0.28);
    }
    .map-node.busy .luggage-marker {
      background: #ffb020;
      box-shadow: 0 0 20px rgba(255, 176, 32, 0.76);
    }
    .map-node.alert {
      border-color: rgba(255, 92, 122, 0.52);
      box-shadow: 0 0 0 8px rgba(255, 92, 122, 0.07), 0 16px 36px rgba(0, 0, 0, 0.28);
    }
    .map-node.alert .luggage-marker {
      background: #ff5c7a;
      box-shadow: 0 0 22px rgba(255, 92, 122, 0.76);
    }
    .map-node.selected {
      border-color: #8defff;
      background: rgba(10, 30, 52, 0.96);
      box-shadow: 0 0 0 10px rgba(141, 239, 255, 0.1), 0 0 34px rgba(141, 239, 255, 0.22);
    }
    .map-node.selected::after {
      content: "";
      position: absolute;
      inset: -6px;
      border-radius: 12px;
      border: 1px solid rgba(141, 239, 255, 0.42);
      pointer-events: none;
    }
    .route-line {
      position: absolute;
      height: 2px;
      border-radius: 999px;
      background: linear-gradient(90deg, transparent, rgba(141, 239, 255, 0.7), transparent);
      transform-origin: left center;
      opacity: 0.68;
    }
    .route-line.one { left: 16%; top: 32%; width: 52%; transform: rotate(16deg); }
    .route-line.two { left: 24%; top: 68%; width: 48%; transform: rotate(-19deg); }
    .route-line.three { left: 48%; top: 26%; width: 34%; transform: rotate(72deg); }
    .map-events {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 8px;
      margin-bottom: 10px;
    }
    .map-events article {
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 42px;
      padding: 9px 10px;
      border-radius: 8px;
      color: rgba(238, 247, 255, 0.76);
      background: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(141, 239, 255, 0.11);
      font-size: 0.74rem;
      font-weight: 900;
    }
    .map-events lucide-icon {
      color: #13d8a7;
      flex: 0 0 auto;
    }
    .map-events .warning lucide-icon {
      color: #ffb020;
    }
    .node-detail {
      display: grid;
      grid-template-columns: 0.72fr 1fr auto;
      gap: 12px;
      align-items: center;
      min-height: 70px;
      margin-bottom: 10px;
      padding: 12px;
      border-radius: 8px;
      color: rgba(238, 247, 255, 0.8);
      background:
        linear-gradient(135deg, rgba(19, 216, 167, 0.12), rgba(255, 255, 255, 0.055));
      border: 1px solid rgba(19, 216, 167, 0.22);
    }
    .node-detail span {
      display: block;
      color: rgba(238, 247, 255, 0.5);
      font-size: 0.7rem;
      font-weight: 900;
      text-transform: uppercase;
    }
    .node-detail strong {
      display: block;
      margin-top: 4px;
      color: white;
      font-family: "Space Grotesk", sans-serif;
      font-size: 1rem;
      line-height: 1.05;
    }
    .node-detail p {
      margin: 0;
      color: rgba(238, 247, 255, 0.7);
      font-size: 0.78rem;
      font-weight: 800;
      line-height: 1.35;
    }
    .node-detail > small {
      padding: 8px 10px;
      border-radius: 999px;
      color: #07111f;
      background: #13d8a7;
      font-size: 0.72rem;
      font-weight: 950;
      white-space: nowrap;
    }
    .node-detail.busy {
      border-color: rgba(255, 176, 32, 0.3);
      background: linear-gradient(135deg, rgba(255, 176, 32, 0.13), rgba(255, 255, 255, 0.055));
    }
    .node-detail.busy > small {
      background: #ffb020;
    }
    .node-detail.alert {
      border-color: rgba(255, 92, 122, 0.34);
      background: linear-gradient(135deg, rgba(255, 92, 122, 0.14), rgba(255, 255, 255, 0.055));
    }
    .node-detail.alert > small {
      color: white;
      background: #ff5c7a;
    }
    .telemetry-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
    }
    .telemetry-grid article {
      min-height: 76px;
      padding: 12px;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.07);
      border: 1px solid rgba(141, 239, 255, 0.12);
    }
    .telemetry-grid span {
      display: block;
      color: rgba(238, 247, 255, 0.58);
      font-size: 0.78rem;
      font-weight: 850;
    }
    .telemetry-grid strong {
      display: block;
      margin-top: 8px;
      color: #eef7ff;
      font-family: "Space Grotesk", sans-serif;
      font-size: 1.35rem;
    }
    .problem-section {
      color: #091327;
      background: #eef7fb;
    }
    .problem-grid {
      display: grid;
      grid-template-columns: 0.9fr 1.1fr;
      gap: 42px;
      align-items: start;
    }
    .problem-copy {
      display: grid;
      gap: 18px;
      color: #344054;
      font-size: 1.08rem;
      font-weight: 760;
      line-height: 1.7;
    }
    .problem-copy p {
      margin: 0;
    }
    .problem-goals {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
      margin-top: 8px;
    }
    .problem-goals article {
      position: relative;
      min-height: 72px;
      display: flex;
      align-items: center;
      padding: 14px 14px 14px 38px;
      border-radius: 8px;
      border: 1px solid rgba(9, 19, 39, 0.09);
      background: rgba(255, 255, 255, 0.82);
      color: #091327;
      box-shadow: 0 14px 34px rgba(9, 19, 39, 0.07);
      font-weight: 900;
      line-height: 1.34;
    }
    .problem-goals article::before {
      content: "";
      position: absolute;
      left: 16px;
      top: 50%;
      width: 10px;
      height: 10px;
      border-radius: 999px;
      background: #13d8a7;
      box-shadow: 0 0 18px rgba(19, 216, 167, 0.65);
      transform: translateY(-50%);
    }
    .security-hub,
    .video-command {
      padding: 94px 0;
      background:
        radial-gradient(circle at 80% 12%, rgba(19, 216, 167, 0.11), transparent 22rem),
        linear-gradient(180deg, #07111f, #0b1323);
    }
    .section-heading {
      max-width: 760px;
      margin-bottom: 30px;
    }
    .security-hub .section-title,
    .video-command .section-title {
      color: white;
    }
    .hub-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 16px;
    }
    .hub-card {
      min-height: 245px;
      padding: 24px;
      border-radius: 8px;
      border: 1px solid rgba(141, 239, 255, 0.13);
      background:
        linear-gradient(145deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.035)),
        radial-gradient(circle at 85% 0%, rgba(19, 216, 167, 0.12), transparent 12rem);
      box-shadow: 0 18px 60px rgba(0, 0, 0, 0.18);
    }
    .hub-card h3 {
      margin: 18px 0 10px;
      color: #eef7ff;
      font-family: "Space Grotesk", sans-serif;
      font-size: 1.2rem;
    }
    .hub-card p {
      margin: 0;
      color: rgba(238, 247, 255, 0.68);
      font-weight: 760;
      line-height: 1.58;
    }
    .video-grid {
      display: grid;
      grid-template-columns: 0.82fr 1.18fr;
      gap: 38px;
      align-items: center;
    }
    .video-tabs {
      display: grid;
      gap: 10px;
      margin-top: 26px;
    }
    .video-tabs button {
      display: flex;
      align-items: center;
      gap: 10px;
      min-height: 52px;
      padding: 12px 14px;
      border-radius: 8px;
      border: 1px solid rgba(141, 239, 255, 0.14);
      color: rgba(238, 247, 255, 0.78);
      background: rgba(255, 255, 255, 0.06);
      cursor: pointer;
      font-weight: 900;
      text-align: left;
    }
    .video-tabs button.active {
      color: #07111f;
      background: #8defff;
    }
    .video-frame {
      position: relative;
      overflow: hidden;
      border-radius: 8px;
      border: 1px solid rgba(141, 239, 255, 0.18);
      box-shadow: 0 32px 100px rgba(0, 0, 0, 0.35);
    }
    .video-frame video {
      display: block;
      width: 100%;
      aspect-ratio: 16 / 9;
      object-fit: cover;
      background: #07111f;
    }
    .video-caption {
      position: absolute;
      left: 16px;
      right: 16px;
      bottom: 16px;
      padding: 14px 16px;
      border-radius: 8px;
      background: rgba(5, 12, 22, 0.76);
      border: 1px solid rgba(141, 239, 255, 0.16);
      backdrop-filter: blur(14px);
    }
    .video-caption span {
      display: block;
      color: #13d8a7;
      font-size: 0.78rem;
      font-weight: 900;
      text-transform: uppercase;
    }
    .video-caption strong {
      display: block;
      margin-top: 5px;
      color: white;
      font-family: "Space Grotesk", sans-serif;
      font-size: 1.15rem;
    }
    .future-section {
      padding: 86px 0;
      background:
        radial-gradient(circle at 22% 16%, rgba(19, 216, 167, 0.18), transparent 22rem),
        radial-gradient(circle at 80% 40%, rgba(19, 93, 255, 0.16), transparent 24rem),
        #07111f;
    }
    .future-panel {
      position: relative;
      overflow: hidden;
      padding: 42px;
      border-radius: 8px;
      border: 1px solid rgba(141, 239, 255, 0.18);
      background:
        linear-gradient(135deg, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.035)),
        linear-gradient(90deg, rgba(19, 216, 167, 0.1), rgba(19, 93, 255, 0.08));
      box-shadow: 0 28px 90px rgba(0, 0, 0, 0.28);
    }
    .future-panel::after {
      content: "";
      position: absolute;
      inset: auto -12% -40% 42%;
      height: 220px;
      border-radius: 999px;
      background: rgba(141, 239, 255, 0.12);
      filter: blur(36px);
      pointer-events: none;
    }
    .future-panel h2 {
      position: relative;
      max-width: 860px;
      margin: 12px 0 16px;
      color: white;
      font-family: "Space Grotesk", sans-serif;
      font-size: clamp(2rem, 4vw, 4.1rem);
      line-height: 0.98;
      letter-spacing: 0;
    }
    .future-panel p {
      position: relative;
      max-width: 760px;
      margin: 0;
      color: rgba(238, 247, 255, 0.72);
      font-size: 1.08rem;
      font-weight: 760;
      line-height: 1.68;
    }
    .mission-section {
      padding: 96px 0;
      color: #091327;
      background:
        linear-gradient(135deg, rgba(19, 93, 255, 0.12), rgba(19, 216, 167, 0.1)),
        #f7fbff;
    }
    .mission-panel {
      padding: 42px;
      border-radius: 8px;
      border: 1px solid rgba(9, 19, 39, 0.1);
      background: rgba(255, 255, 255, 0.88);
      box-shadow: var(--vx-shadow);
    }
    .mission-panel h2 {
      max-width: 900px;
      margin: 12px 0 16px;
      font-family: "Space Grotesk", sans-serif;
      font-size: clamp(2rem, 4vw, 4.4rem);
      line-height: 0.98;
      letter-spacing: 0;
    }
    .mission-panel p {
      max-width: 820px;
      margin: 0;
      color: #344054;
      font-size: 1.08rem;
      font-weight: 760;
      line-height: 1.72;
    }
    .mission-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      margin-top: 28px;
    }
    @media (max-width: 980px) {
      .hero-grid,
      .problem-grid,
      .video-grid {
        grid-template-columns: 1fr;
      }
      .command-panel {
        min-height: 460px;
      }
      .hub-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }
    @media (max-width: 640px) {
      .cinematic-hero {
        min-height: auto;
      }
      .hero-grid {
        padding: 52px 0;
      }
      .signal-strip,
      .telemetry-grid,
      .map-events,
      .node-detail,
      .problem-goals,
      .hub-grid {
        grid-template-columns: 1fr;
      }
      .command-panel {
        min-height: auto;
      }
      .status-map {
        height: 330px;
      }
      .security-hub,
      .video-command,
      .future-section,
      .mission-section {
        padding: 68px 0;
      }
      .future-panel,
      .mission-panel {
        padding: 26px;
      }
    }
  `]
})
export class HomeAlternativeComponent implements AfterViewInit, OnDestroy {
  @ViewChild('pageRoot') private pageRoot?: ElementRef<HTMLElement>;
  @ViewChild('heroVideo') private heroVideo?: ElementRef<HTMLVideoElement>;

  protected readonly activeVideo = signal(0);
  protected readonly selectedNode = signal<CityNode>({
    name: 'Stazione centrale',
    label: '18 liberi',
    detail: 'Hub ad alta rotazione per viaggiatori, pendolari e deposito bagagli 24/7.',
    x: 18,
    y: 34,
    state: 'active'
  });
  private animationContext?: gsap.Context;

  protected readonly videos = [
    { src: 'assets/media/vaultrix-home-01.mp4', title: 'Deposito bagagli digitale', kicker: 'Step 01' },
    { src: 'assets/media/vaultrix-home-02.mp4', title: 'Accesso smart locker', kicker: 'Step 02' },
    { src: 'assets/media/vaultrix-home-03.mp4', title: 'Esperienza VaulTriX', kicker: 'Step 03' }
  ];

  protected readonly heroSignals = [
    { value: '24/7', label: 'Protezione continua' },
    { value: 'QR', label: 'Accesso digitale' },
    { value: 'Live', label: 'Monitoraggio smart' },
    { value: 'City', label: 'Smart City ready' }
  ];

  protected readonly telemetry = [
    { label: 'Locker attivi', value: '128' },
    { label: 'Accessi oggi', value: '2.4k' },
    { label: 'Alert aperti', value: '02' },
    { label: 'Uptime rete', value: '99.8%' }
  ];

  protected readonly birthGoals = [
    'Proteggere gli effetti personali 24/7',
    'Ridurre il rischio di furti e smarrimenti',
    'Supportare la sicurezza urbana',
    'Migliorare l’esperienza di turisti e cittadini',
    'Integrarsi con le future Smart City'
  ];

  protected readonly cityNodes = [
    {
      name: 'Stazione centrale',
      label: '18 liberi',
      detail: 'Hub ad alta rotazione per viaggiatori, pendolari e deposito bagagli 24/7.',
      x: 18,
      y: 34,
      state: 'active'
    },
    {
      name: 'Centro storico',
      label: 'Alta occup.',
      detail: 'Nodo turistico con domanda elevata e accessi concentrati nelle fasce centrali.',
      x: 34,
      y: 62,
      state: 'busy'
    },
    {
      name: 'Polo eventi',
      label: '42 accessi/h',
      detail: 'Stazione temporanea per fiere, concerti e grandi flussi in ingresso.',
      x: 78,
      y: 34,
      state: 'active'
    },
    {
      name: 'Aeroporto',
      label: 'Verifica nodo',
      detail: 'Controllo operativo richiesto su sensore apertura e stato anti-manomissione.',
      x: 72,
      y: 72,
      state: 'alert'
    },
    {
      name: 'Terminal bus',
      label: '24/7 attivo',
      detail: 'Punto intermodale sempre disponibile per deposito rapido e ritiro autonomo.',
      x: 22,
      y: 78,
      state: 'active'
    }
  ] satisfies CityNode[];

  protected readonly securityHub = [
    {
      icon: 'qr-code',
      title: 'Accesso QR e app',
      text: 'Apertura rapida tramite codice digitale, PIN temporaneo o app mobile.'
    },
    {
      icon: 'fingerprint',
      title: 'Autenticazione avanzata',
      text: 'Controlli di accesso progettati per ridurre utilizzi non autorizzati.'
    },
    {
      icon: 'radar',
      title: 'Monitoraggio real-time',
      text: 'Stato della rete, occupazione e anomalie visibili da pannello operativo.'
    },
    {
      icon: 'shield-check',
      title: 'Anti-manomissione',
      text: 'Protezione fisica e digitale contro aperture improprie e tentativi di forzatura.'
    },
    {
      icon: 'file-clock',
      title: 'Tracciabilità accessi',
      text: 'Log digitali di apertura, chiusura e utilizzo per operatori e partner.'
    },
    {
      icon: 'network',
      title: 'Integrazione urbana',
      text: 'Compatibile con infrastrutture pubbliche, private e scenari Smart City.'
    }
  ];

  ngAfterViewInit() {
    const root = this.pageRoot?.nativeElement;
    if (!root) return;

    this.animationContext = gsap.context(() => {
      gsap.from('.brand-lockup, h1, .hero-copy p, .hero-actions, .signal-strip article', {
        y: 26,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.08
      });

      gsap.from('.command-panel', {
        x: 34,
        opacity: 0,
        duration: 1,
        delay: 0.25,
        ease: 'power3.out'
      });

      gsap.to('.map-node', {
        scale: 1.16,
        duration: 1.6,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
        stagger: 0.22
      });

      gsap.to('.route-line', {
        opacity: 0.18,
        duration: 1.8,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
        stagger: 0.28
      });
    }, root);
  }

  ngOnDestroy() {
    this.animationContext?.revert();
  }

  protected playNextVideo() {
    this.activeVideo.update((index) => (index + 1) % this.videos.length);
  }

  protected selectVideo(index: number) {
    this.activeVideo.set(index);
    window.setTimeout(() => this.playCurrentVideo());
  }

  protected selectNode(node: CityNode) {
    this.selectedNode.set(node);
  }

  protected playCurrentVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;
    video.muted = true;
    void video.play();
  }
}

type CityNode = {
  name: string;
  label: string;
  detail: string;
  x: number;
  y: number;
  state: 'active' | 'busy' | 'alert';
};
